/**
 * Properties (Liegenschaften) domain API functions (Step 7.1.12).
 *
 * List, get, create, update properties; geocoding.
 * NOT landlord-properties relation (that's in landlords.js).
 * NOT property documents (that's in documents.js).
 * Extracted from adminData/index.js for team scalability.
 */
import { API_BASE_URL, getApiHeaders, expectPaginatedItems } from "./core";

/**
 * Properties (Phase D). List, get, create, update.
 */
export function fetchAdminProperties() {
  return fetch(`${API_BASE_URL}/api/admin/properties`, { headers: getApiHeaders() }).then((res) => {
    if (!res.ok) throw new Error("Liegenschaften konnten nicht geladen werden.");
    return res.json();
  });
}

/**
 * GET /api/admin/properties/picker-options (D4a2) — the ``{items, total}``
 * envelope the async pickers read.
 *
 * ``fetchAdminProperties`` above sends no parameters and returns a bare
 * array, so it can neither search nor page and is unusable as a picker
 * source — hence a second function rather than a change to it, which
 * several admin tables consume as an array.
 *
 * A blank term is omitted entirely rather than sent as an empty string:
 * the server treats both the same, but not sending it keeps the request
 * honest about what was actually asked.
 */
export function fetchAdminPropertyPickerOptions({ q, limit, offset } = {}) {
  const sp = new URLSearchParams();
  if (q != null && String(q).trim()) sp.set("q", String(q).trim());
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/properties/picker-options${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) {
      throw new Error("Liegenschaften konnten nicht geladen werden.");
    }
    const data = await res.json();
    const items = expectPaginatedItems(
      data,
      "GET /api/admin/properties/picker-options"
    );
    return {
      items,
      total: typeof data?.total === "number" ? data.total : items.length,
    };
  });
}

export function fetchAdminProperty(id) {
  return fetch(`${API_BASE_URL}/api/admin/properties/${encodeURIComponent(id)}`, {
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error("Liegenschaft konnte nicht geladen werden.");
    }
    return res.json();
  });
}

export function createAdminProperty(body) {
  return fetch(`${API_BASE_URL}/api/admin/properties`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error("Liegenschaft konnte nicht erstellt werden.");
    return res.json();
  });
}

export function updateAdminProperty(id, body) {
  return fetch(`${API_BASE_URL}/api/admin/properties/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  }).then((res) => {
    if (!res.ok) throw new Error("Liegenschaft konnte nicht gespeichert werden.");
    return res.json();
  });
}

/**
 * POST /api/admin/properties/{id}/geocode — manual geocoding retry (server-side).
 */
export function geocodeAdminProperty(id) {
  return fetch(`${API_BASE_URL}/api/admin/properties/${encodeURIComponent(id)}/geocode`, {
    method: "POST",
    headers: getApiHeaders(),
  }).then((res) => {
    if (!res.ok) throw new Error("Geocoding-Anfrage fehlgeschlagen.");
    return res.json();
  });
}
